import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { unlimitedDocsFakedoorConfig, unlimitedDocumentsLogger } from 'reactApp/appHelpers/featuresHelpers/features/unlimitedDocuments';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import type { UnlimitedDocumentsTariffType } from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.const';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { getUnlimitedDocumentsAsidePromoCoverImageUrl } from 'reactApp/ui/UnlimitedDocuments/UnlimitedDocumentsAsidePromo/UnlimitedDocumentsAsidePromo.helpers';

const PROMO_ID = 'unlim-docs-aside';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/UnlimitedDocuments/UnlimitedDocumentsAsidePromo/UnlimitedDocumentsAsidePromo'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const unlimitedDocumentsAsidePromoCreator: ModelCreator = ({ models, render, stores }) => {
    const tariffType = unlimitedDocsFakedoorConfig.type;

    return new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps<{ type: UnlimitedDocumentsTariffType }>(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            isModelEnabledCheck: () => {
                return unlimitedDocsFakedoorConfig.asidePromo.enable || false;
            },
            showLimit: unlimitedDocsFakedoorConfig.asidePromo.showLimit || 0,
            closeLimit: unlimitedDocsFakedoorConfig.asidePromo.closeLimit || 0,
            showInterval: unlimitedDocsFakedoorConfig.asidePromo.showInterval || 0,
            templateData: {
                type: unlimitedDocsFakedoorConfig.type,
            },
            ignoreTestOnRedraw: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                const { isHome } = getStorage(storage);
                const isPaidUser = UserSelectors.isPaidUser(state);

                const conditions = {
                    isNotHome: !isHome,
                    isPaidUser,
                };

                if (Object.values(conditions).includes(true)) {
                    unlimitedDocumentsLogger.info(`${PROMO_ID} init failure`, conditions);
                    reject();
                    return;
                }

                resolve();
            },
            preloadingImages: [getUnlimitedDocumentsAsidePromoCoverImageUrl[tariffType]],
        })
    );
};
